/**
 * @fileoverview General helper utilities
 * @module utils/helpers
 */

import Swal from 'sweetalert2';

/**
 * Generate random alphanumeric string
 * @param {number} length - Length of string to generate
 * @returns {string} Random string
 *
 * @example
 * random_charactor(8) // "a1B2c3D4"
 */
export const random_charactor = (length) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length }, () =>
    characters.charAt(Math.floor(Math.random() * characters.length))
  ).join('');
};

/**
 * Copy text to clipboard
 * @param {string} textToCopy - Text to copy
 * @returns {Promise} Resolves when copied
 */
export const copyText = (textToCopy) => {
  return navigator.clipboard
    .writeText(textToCopy)
    .then(() => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Copied successfully',
        showConfirmButton: false,
        timer: 1500,
        background: '#fff',
        color: '#000000',
        confirmButtonColor: '#212529',
        cancelButtonColor: 'unset',
      });
    })
    .catch((err) => {
      console.error('Failed to copy:', err);
    });
};

/**
 * Get URL query parameter
 * @param {string} name - Parameter name
 * @returns {string|null} Parameter value
 *
 * @example
 * // URL: /page?id=123
 * params('id') // "123"
 */
export const params = (name) => {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get(name);
};

/**
 * Required field indicator JSX
 */
export const required = (<span class="text-danger">*</span>);

/**
 * Debounce function execution
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function execution
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Sleep/delay function
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise} Resolves after delay
 */
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 * @param {*} value - Value to check
 * @returns {boolean} True if empty
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

const helpers = {
  random_charactor,
  copyText,
  params,
  required,
  debounce,
  throttle,
  sleep,
  isEmpty,
};

export default helpers;
